import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import JsonLd from '@/components/base/JsonLd';
import { siteInfo } from '@/mocks/site';

export default function AboutPage() {
  const contactRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (window.location.hash === '#contact' && contactRef.current) {
      setTimeout(() => {
        contactRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 逼特逼视频官方平台"
        description={`了解${siteInfo.name}的品牌故事、平台声誉、核心团队及联系方式。${siteInfo.description}`}
        keywords="逼特逼视频关于我们,逼特逼视频品牌,逼特逼视频联系方式,逼特逼视频官方"
        canonicalUrl="https://www.bitbivideo.com/about"
      />
      <JsonLd type="Organization" />
      <JsonLd type="WebSite" />
      <JsonLd
        type="WebPage"
        pageTitle="关于我们 - 逼特逼视频"
        pageDescription={`了解${siteInfo.name}的品牌故事、平台声誉及联系方式`}
        pageUrl="https://www.bitbivideo.com/about"
      />
      <JsonLd
        type="BreadcrumbList"
        breadcrumbs={[
          { name: '首页', url: 'https://www.bitbivideo.com' },
          { name: '关于我们', url: 'https://www.bitbivideo.com/about' },
        ]}
      />

      <Navbar />
      <Breadcrumb />

      <main>
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-4">
                关于逼特逼视频
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
                致力于打造全球领先的视频内容平台，让每个人都能发现和分享精彩
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  逼特逼视频成立于{siteInfo.foundingYear}年，由一群热爱视频内容的技术极客和创意人才共同创建。我们相信视频是表达创意、分享知识、连接世界最强大的媒介。从最初的创业团队到如今拥有数百名员工的科技公司，逼特逼视频始终秉持"用视频连接世界"的使命。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  平台上线以来，我们已经服务了超过2.8亿月活用户，日均视频播放量突破15亿次。超过500万内容创作者在我们的平台上分享他们的创意、知识和生活。我们不仅是中国领先的视频平台，更在全球150多个国家和地区拥有活跃用户。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们的核心团队来自全球顶尖互联网企业，在人工智能、大数据、视频编码和内容推荐等领域拥有深厚的技术积累。凭借强大的技术实力和对用户需求的深刻理解，逼特逼视频持续引领视频内容平台的发展方向。
                </p>
              </div>
              <div className="w-full h-72 md:h-96 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20collaborative%20team%20working%20on%20screens%2C%20warm%20ambient%20lighting%20through%20floor%20to%20ceiling%20windows%2C%20plants%20and%20wooden%20furniture%2C%20professional%20yet%20relaxed%20atmosphere%2C%20editorial%20photography&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="逼特逼视频团队"
                  className="w-full h-full object-cover object-top"
                  title="逼特逼视频团队"
                />
              </div>
            </div>

            <div id="reputation" className="mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                平台声誉与成就
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
                {siteInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-600">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-award-line text-xl text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                        行业认可
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        连续三年荣获"最佳视频平台"称号，2023年获得"科技创新企业奖"，2024年入选"最具影响力互联网平台"榜单。被多家权威媒体评为"用户满意度最高的视频内容平台"。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-star-line text-xl text-secondary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                        技术实力
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        拥有超过200项技术专利，自主研发的视频编码技术将带宽消耗降低40%同时保持超清画质。AI推荐算法获得多项国际技术大奖，内容安全审核系统通过国家信息安全等级保护认证。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-heart-pulse-line text-xl text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                        社会责任
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        积极践行企业社会责任，发起"乡村教育视频计划"覆盖超过1000所乡村学校。设立创作者公益基金，支持优质教育、科普内容的创作与传播。累计捐赠教育设备价值超过5000万元。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-group-line text-xl text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                        用户口碑
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        平台综合评分4.8分（满分5分），用户满意度高达96%。在各大应用商店长期保持4.5星以上评价。累计收到用户好评超过5000万条，是用户最信赖的视频内容平台之一。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <section ref={contactRef} id="contact">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                    联系方式
                  </h3>
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800 mb-0.5">公司地址</p>
                        <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-lg text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800 mb-0.5">客服电话</p>
                        <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-lg text-secondary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800 mb-0.5">电子邮箱</p>
                        <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800 mb-0.5">工作时间</p>
                        <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                    发送消息
                  </h3>
                  <form
                    data-readdy-form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                      if (honeypot) {
                        alert('消息已发送，我们将尽快回复您！');
                        form.reset();
                        return;
                      }

                      const formData = new FormData(form);
                      try {
                        const res = await fetch('https://readdy.ai/api/form/d9q54lue3oq4jqumtgi0', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const resText = await res.text();
                        let parsed: Record<string, unknown> | null = null;
                        try { parsed = JSON.parse(resText); } catch { /* ignore */ }
                        if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
                          alert('消息已发送，我们将尽快回复您！');
                          form.reset();
                        } else {
                          const msg = (parsed as Record<string, unknown>)?.meta
                            ? ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message
                            : '发送失败，请稍后重试';
                          alert(typeof msg === 'string' ? msg : '发送失败，请稍后重试');
                        }
                      } catch {
                        alert('网络错误，请稍后重试');
                      }
                    }}
                    className="flex flex-col gap-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        消息内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息内容（最多500字）"
                      ></textarea>
                    </div>
                    <div className="honeypot-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200 self-start"
                    >
                      <i className="ri-send-plane-line mr-1.5"></i>
                      发送消息
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}